import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import FeedbackRatingIcon from 'components/FeedbackRatingIcon'
import TabPane from 'components/Tabs/TabPane'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { Fields as UserProfile } from 'models/UserProfile'
import React, { useState } from 'react'
import { useAbility } from 'utilities/ability'
import ButtonPanel from '../ButtonPanel'
import UserCampaigns from './Campaigns'
import Forbidden from './Forbidden'
import ItemsPurchased from './ItemsPurchased'
import OrderFeedback from './OrderFeedback'
import Orders from './Orders'
import Overview from './Overview'
import PointsExpiration from './PointsExpiration'
import Rewards from './Rewards'

const t = buildTranslate('users.view.activity')

type Props = {
  profile: UserProfile | null
}

// S1 (RecoveryAI Customer Profile Page design spec, CRM Figma): the nav
// thumbs are 15px, smaller than the 20px default every table cell renders
// FeedbackRatingIcon at.
const NAV_THUMB_SIZE_PX = 15

type PanelType =
  | 'overview'
  | 'items_purchased'
  | 'rewards'
  | 'feedback'
  | 'campaigns'
  | 'orders'
  | 'points_expiration'

const Activity: React.FC<Props> = ({ profile }: Props) => {
  const [selectedPanel, setSelectedPanel] = useState<PanelType>('overview')
  const [css] = useStyletron()
  const isEmailsTabEnabled = useFlag('MX4-UserProfiles-EmailsTab', false)

  const hasAbility = useAbility().can('access', 'Users')

  // S1: a green thumbs-up then a red thumbs-down after the label, 15x15 each
  // with a 3px left margin — the "Order feedback" nav item only. Every other
  // panel keeps a plain string label (ButtonPanel renders every other item
  // exactly as before).
  const navThumbClass = css({
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginLeft: '3px',
  })

  const panels: Array<PanelType> = [
    'overview',
    'orders',
    'items_purchased',
    'rewards',
    'points_expiration',
    'feedback',
  ]

  if (isEmailsTabEnabled) {
    panels.push('campaigns')
  }

  if (!profile) {
    return null
  }

  if (!hasAbility) {
    return (
      <TabPane state="activity">
        <Forbidden />
      </TabPane>
    )
  }

  return (
    <TabPane state={'activity'}>
      <section
        className={`mb-l ${css({
          minHeight: 'calc(100vh - 300px)',
        })}`}
      >
        <Text.Header3 className="my-0">{t('title')}</Text.Header3>
        <div className={'d-flex mt-xl'}>
          <ButtonPanel
            selectedPanel={selectedPanel}
            onSelectPanel={setSelectedPanel}
            panels={panels.map(panel => ({
              type: panel,
              label:
                panel === 'feedback' ? (
                  <>
                    {t(`panels.${panel}`)}
                    <span className={navThumbClass}>
                      <FeedbackRatingIcon
                        rating="up"
                        size={NAV_THUMB_SIZE_PX}
                      />
                    </span>
                    <span className={navThumbClass}>
                      <FeedbackRatingIcon
                        rating="down"
                        size={NAV_THUMB_SIZE_PX}
                      />
                    </span>
                  </>
                ) : (
                  t(`panels.${panel}`)
                ),
            }))}
          />
          <div className={'w-100'}>
            {selectedPanel === 'overview' && <Overview profile={profile} />}
            {selectedPanel === 'items_purchased' && (
              <ItemsPurchased profile={profile} />
            )}
            {selectedPanel === 'rewards' && <Rewards profile={profile} />}
            {selectedPanel === 'orders' && <Orders profile={profile} />}
            {selectedPanel === 'points_expiration' && (
              <PointsExpiration profile={profile} />
            )}
            {/* AI-1288 (FR-29/30/31): the 'feedback' panel slot now renders
                RecoveryAI order feedback, not the legacy NPS Feedback panel
                (kept at ./Feedback, unreachable but not deleted — see its
                locale comment). */}
            {selectedPanel === 'feedback' && (
              <OrderFeedback profile={profile} />
            )}
            {selectedPanel === 'campaigns' && (
              <UserCampaigns profile={profile} />
            )}
          </div>
        </div>
      </section>
    </TabPane>
  )
}

export default Activity
